import { Button, Drawer, Flex } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { assignProjectToGeneratableDocumentTemplate } from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import React from 'react';
import { DealBasicDataTct } from '@dealstack/shared/lib/generated';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Select } from 'react-hook-form-mantine';

interface Props {
	opened: boolean;
	onClose: () => void;
	templateId: string;
	availableDeals: DealBasicDataTct[];
	refetchLinkedProjects: () => void;
}

export const LinkNewProjectDrawer = ({
	opened,
	onClose,
	templateId,
	availableDeals,
	refetchLinkedProjects,
}: Props) => {
	const assignProjectRequest = useMutation({
		mutationFn: assignProjectToGeneratableDocumentTemplate,
	});

	const form = useForm({
		mode: 'all',
		defaultValues: { selectedDealIdToAssign: '' },
		resolver: zodResolver(z.object({ selectedDealIdToAssign: z.string().nonempty() })),
	});

	const onAssignProject = (shouldClose: boolean) => {
		const dealId = form.getValues().selectedDealIdToAssign;

		assignProjectRequest.mutate(
			{ templateId, dealId },
			{
				onSuccess: async () => {
					showToastV2({ title: 'Project linked successfully' });
					refetchLinkedProjects();
					form.reset();
					if (shouldClose) {
						onClose();
					}
				},
			}
		);
	};

	const projectsOptions = availableDeals.map((project) => ({
		label: project.name,
		value: project.id,
	}));

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			title="Link new project to template"
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			position="right"
		>
			<Flex direction="column" gap="0.5rem" h="100%">
				<Select
					control={form.control}
					name="selectedDealIdToAssign"
					data={projectsOptions}
					label="Select project"
					placeholder="Select project to link"
					searchable
					size="md"
					disabled={assignProjectRequest.isPending}
				/>

				<Button
					mt="auto"
					onClick={form.handleSubmit(() => onAssignProject(true))}
					disabled={assignProjectRequest.isPending}
				>
					Confirm
				</Button>

				<Button
					variant="outline"
					onClick={form.handleSubmit(() => onAssignProject(false))}
					disabled={assignProjectRequest.isPending}
				>
					Confirm and link another
				</Button>
			</Flex>
		</Drawer>
	);
};
